import EnumWalletTypes from "@wallet-manager/node-types/dist/types/postgres/const/WalletType";
import BigNumber from "bignumber.js";
import { isEmpty } from "lodash";
import { Dispatch, SetStateAction, useCallback } from "react";
import AddressBookSuggestion from "src/components/AddressBookSuggestion";
import MpTextFieldNumberOnly from "src/components/MpTextFieldNumberOnly";
import { Box } from "src/components/MuiGenerals";
import RiskTypeSingleSelection from "src/components/RiskTypeSingleSelection";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import {
  AddressBookFilterType,
  createAddressBookFilterConfig,
} from "src/utils/AddressBookFilter/createAddressBookFilter";
import { AddressBookOrder } from "src/views/AssetManagement/Swap/types";
import { TableColumnKey } from "../../../../ContractSweepRecipient/types/Table";
import { ViewGroupData } from "../../../types/ViewGroupData";
import { AddRecipientFields } from "../types/AddRecipientFields";
import { findChainInfo } from "src/utils/helper";

type Props = {
  viewGroupData: ViewGroupData;
  fields: AddRecipientFields;
  setFields: Dispatch<SetStateAction<AddRecipientFields>>;
};

const AddRecordDialogContent = (props: Props) => {
  const { viewGroupData, fields, setFields } = props;
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, tc, translate } = useTranslation(translatePrefix);
  const { chainType, chainId } = viewGroupData;

  const ADDRESS_BOOK_SUGGESTION_FILTER_CONFIG = [
    createAddressBookFilterConfig(AddressBookFilterType.ContractHotWallet, {
      t: translate,
    }),
    createAddressBookFilterConfig(AddressBookFilterType.SweepDestWallet, {
      t: translate,
    }),
  ];

  const updateWalletAddress = useCallback(
    (name: keyof AddressBookOrder, value: string) => {
      if (name === "to_address" || name === "rawItem") {
        return setFields((prevFields: any) => ({
          ...prevFields,
          ...(name === "to_address" && {
            ownerAddress: value,
            addressBookOrder: {
              ...prevFields.addressBookOrder,
              [name]: value,
            },
            ...(value === "" ? { ownerAddress: "", rawWalletInfo: null } : {}),
          }),
          ...(name === "rawItem" && {
            rawWalletInfo: value,
          }),
        }));
      }
    },
    [setFields],
  );

  return (
    <Box
      sx={{
        "& .MuiFormControl-root": {
          width: "100%",
        },
      }}>
      <AddressBookSuggestion
        item={fields.addressBookOrder}
        isAllowFreeInput={false}
        isDisabled={false}
        isHideAllWallet
        filterConfigs={ADDRESS_BOOK_SUGGESTION_FILTER_CONFIG}
        walletTypes={[String(EnumWalletTypes.SweepDestWallet)]}
        chain_name={
          findChainInfo(chainType?.toString() ?? "", chainId ?? "")
            ?.chain_code ?? ""
        }
        setField={updateWalletAddress}
        defaultTabId={AddressBookFilterType.ContractHotWallet}
        config={{
          placeHolder: tc("phInputField", {
            fieldName: t(TableColumnKey.RecipientAddress),
          }),
        }}
      />
      <RiskTypeSingleSelection
        value={fields.riskType}
        onChange={(e) => setFields((f) => ({ ...f, riskType: e.target.value }))}
        onClearSelect={() => {}}
      />
      <MpTextFieldNumberOnly
        label={tc("phSelection", { fieldName: t(TableColumnKey.Rating) })}
        value={fields.probability}
        onChange={(e) => {
          const value = e.target.value;
          if (isEmpty(value)) {
            return setFields((f) => ({ ...f, probability: "" }));
          }
          if (value[value.length - 1] === ".") {
            return;
          }
          if (BigNumber(value).lte(100)) {
            setFields((f) => ({ ...f, probability: value }));
          }
        }}
      />
    </Box>
  );
};

export default AddRecordDialogContent;
